import type { PixelEvent } from '@/events';

import type { DestinationRegistry } from './DestinationRegistry';
import type { DestinationExecutor } from './DestinationExecutor';
import type { DestinationMetrics } from './DestinationMetrics';
import type { DestinationLogger } from './DestinationLogger';
import type { DestinationResult } from './types';

export type DestinationDispatcherConfig = {
  readonly registry: DestinationRegistry;
  readonly executor: DestinationExecutor;
  readonly metrics: DestinationMetrics;
  readonly logger: DestinationLogger;
  readonly concurrency?: number;
};

export class DestinationDispatcher {
  private readonly registry: DestinationRegistry;

  private readonly executor: DestinationExecutor;

  private readonly metrics: DestinationMetrics;

  private readonly logger: DestinationLogger;

  private readonly concurrency: number;

  private lastResults: DestinationResult[] = [];

  constructor(config: DestinationDispatcherConfig) {
    this.registry = config.registry;
    this.executor = config.executor;
    this.metrics = config.metrics;
    this.logger = config.logger;
    const raw = config.concurrency ?? 3;
    this.concurrency = raw <= 0 ? 1 : raw;
  }

  async dispatch(event: PixelEvent): Promise<readonly DestinationResult[]> {
    const destinations = this.registry.supports(event);
    if (destinations.length === 0) {
      this.lastResults = [];
      return [];
    }

    const results: DestinationResult[] = [];

    for (let i = 0; i < destinations.length; i += this.concurrency) {
      const batch = destinations.slice(i, i + this.concurrency);
      const settled = await Promise.allSettled(
        batch.map(adapter => this.executor.execute(adapter, event))
      );

      for (let j = 0; j < settled.length; j += 1) {
        const outcome = settled[j]!;
        const adapter = batch[j]!;

        if (outcome.status === 'fulfilled') {
          results.push(outcome.value);
          this.metrics.record(outcome.value);
        } else {
          const failed: DestinationResult = {
            status: 'failed',
            adapter: adapter.name,
            duration: 0,
            timestamp: Date.now(),
            error:
              outcome.reason instanceof Error
                ? outcome.reason.message
                : String(outcome.reason),
          };
          results.push(failed);
          this.metrics.record(failed);
          this.logger.error(
            `Unhandled destination error: ${adapter.name}`,
            outcome.reason
          );
        }
      }
    }

    this.lastResults = results;
    return results;
  }

  getLastResults(): readonly DestinationResult[] {
    return this.lastResults;
  }

  clearLastResults(): void {
    this.lastResults = [];
  }
}
